import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import Hero from '../../widgets/Hero';
import Summary from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import ConnectStrip from '../../widgets/ConnectStrip';
import FAQ from '../../widgets/FAQ';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import WhySitePen from '../../widgets/WhySitePen';

const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const frontEndImg = require('../../assets/images/icon-illustration/front-end-web-app.svg');
const backEndImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const apiImg = require('../../assets/images/icon-illustration/api-integration.svg');
const databaseDesignImg = require('../../assets/images/icon-illustration/database-development.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const backEndToolsImg = require('../../assets/images/icon-illustration/back-end-tools.svg');
const databaseToolsImg = require('../../assets/images/icon-illustration/database-technology.svg');
const teamImg = require('../../assets/images/illustration/team-efficiency.svg');
const reuseImg = require('../../assets/images/illustration/code-reuse.svg');
const performanceImg = require('../../assets/images/illustration/high-performance.svg');

const factory = create();

export const FullStackDevelopment = factory(function FullStackDevelopment() {
	return (
		<div>
			<Head
				title="Full-Stack JavaScript Development Services | SitePen"
				description="SitePen is an elite full stack JavaScript development company offering enterprise-level services to improve processes, streamline operations, and empower success."
			/>
			<Hero background="level3Purple">
				{{
					xlTitle: (
						<div>
							Full Stack JavaScript
							<br />
							Development
						</div>
					)
				}}
			</Hero>
			<Section type="leading">
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							title: 'Why Choose SitePen as Your Full Stack Development Company?',
							blurb: (
								<virtual>
									<p>
										SitePen was founded on the idea that JavaScript was going to
										change how we interact with web applications. While that
										sounds obvious now, things weren't quite as clear when we
										started this journey 20 years ago when Netscape Navigator
										and Internet Explorer dominated the browser market. We
										immediately started pushing the limits of what JavaScript
										could do and haven't stopped experimenting since.
									</p>
									<p>
										Over time, JavaScript has grown. The language has become
										more powerful and flexible, and the ecosystem has expanded
										due to projects such as Node.js and TypeScript. Today,
										JavaScript has proven itself as one of the only languages
										that can successfully deliver every part of an enterprise
										application from the data layer to the user interface.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Our Development Stack',
							items: [
								{
									blurb: `A software platform's front end applications are almost always the most critical aspect of the system since that is how users access the platform's power. Their role as the user interface, however, also makes them the most challenging to create. To achieve the difficult goal of delivering an excellent user experience in an efficient, maintainable manner, the JavaScript community has developed a wide variety of powerful frameworks such as React, Angular, Vue.js, and Dojo. Our engineers have specialized in and often contributed to these projects giving our team a deep understanding of how to utilize them to deliver spectacular results.`,
									title: `Front End Development`,
									image: (
										<Image src={frontEndImg} lazy size="icon" align="center" />
									)
								},
								{
									blurb: `While front end applications of a software suite may be the most challenging to develop, the back end is the most important to get right. Given their responsibility for codifying business rules and managing large amounts of often sensitive data, back end applications must be developed with an obsessive attention to detail and a rigorous suite of automated tests. Our engineers' skills extend beyond simply knowing how to use Node.js to develop back end systems. They know how to translate complex business rules into clear code that can easily be extended and modified to adapt to evolving business needs.`,
									title: `Back End Development`,
									image: (
										<Image src={backEndImg} lazy size="icon" align="center" />
									)
								},
								{
									blurb: `Modern web applications are, by nature, made up of multiple smaller programs that work together. At the very least, a program on the back end needs to receive and respond to requests from a front end application. Many systems go far beyond this minimum, breaking different business domains amongst multiple microservices. Regardless of how granular the application's components are, ensuring they these services can easily communicate with one another is critical to the success of the entire system. When developing applications, we carefully design the service APIs for each application. We consider transport protocols, service interfaces, payload sizes, and naming conventions to develop highly cohesive, loosely coupled services that scale smoothly as the demands places upon them change.`,
									title: `API Integration`,
									image: <Image src={apiImg} lazy size="icon" align="center" />
								},
								{
									blurb: `Our clients often have a preferred database system that they would like us to use. Our developers have extensive experience with the most popular database management systems, whether cloud-based or on-premises, SQL or NoSQL. We use this deep understanding to create a database schema that maximizes application performance. We don't just design the tables or document formats; we recommend indexing strategies, primary and foreign keys, and the level of normalization that should be adopted to optimize database query times.`,
									title: `Database Design`,
									image: (
										<Image
											src={databaseDesignImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={engineeringImg} size="lg" />,
							title: 'Our Full Stack Development Process',
							blurb: (
								<virtual>
									Utilizing a single team with a single language to deliver the
									full application stack dramatically improves productivity and
									reduces the likelihood of errors occurring at interface
									boundaries. As a result, we believe that this is the best
									approach for many applications. However, we do not think that
									full stack development needs to be taken on individually. Many
									engineers have a natural tendency toward front end or back end
									development. We blend engineers from both sides to create teams
									that combine the benefits of domain specialists with the
									advantages of end-to-end development workflows.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary>
						{{
							title: 'Our Full Stack Development Tool Set',
							blurb: '',
							items: [
								{
									title: 'Front End Tools',
									blurb: (
										<virtual>
											Almost all web applications are composed of HTML, CSS,
											and JavaScript. How each of these is generated is
											exceptionally diverse. To be effective, we emphasize
											flexibility in our toolset - an application written in
											Angular has a dramatically different set of patterns and
											best-practices than a React application. We believe that
											adapting to each framework's idioms and patterns will
											typically result in the most efficient and maintainable
											applications. We also layer in additional tools that
											make applications easier to understand and maintain,
											such as TypeScript.
										</virtual>
									),
									image: (
										<Image
											src={frontEndToolsImg}
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									)
								},
								{
									title: 'Back End Tools',
									blurb: (
										<virtual>
											Monolith or microservices? REST or RPC? These are some
											of the critical decisions that must be made when
											designing a backend system. While we have our opinions
											on what generally works best, we believe that every
											project should be evaluated based on its unique use
											cases and constraints. Our developers lean toward the
											use of RESTful interfaces to communicate between a
											collection of microservices. In our experience, this
											leads to systems that are both flexible and easily
											understood.
										</virtual>
									),
									image: (
										<Image
											src={backEndToolsImg}
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									)
								},
								{
									title: 'Database Technology',
									blurb: (
										<virtual>
											Our customers' database solutions are often the least
											flexible part of their infrastructure. Having a common
											data repository reduces overhead and simplifies report
											generation and other analytical processes. As such, we
											take a generalist approach to database technologies.
											Instead of focusing on a few specific database systems,
											we focus on mastering best-practices that apply to a
											wide variety of database management systems, whether
											they are cloud-based or in-house.
										</virtual>
									),
									image: (
										<Image
											src={databaseToolsImg}
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating"
						trailingBias="medium"
						listGuttering="none"
						itemPaddingBottom="md"
					>
						{{
							title: 'The Benefits of Full Stack JavaScript Development',
							items: [
								{
									blurb: `There are numerous advantages to having an application written in a common language. Development teams can have more flexibility when assigning tasks; code reviews are more effective and more likely to identify areas for improvement; and software modules can often be shared between layers, reducing the likelihood of inter-service errors. Long term maintenance of such applications is often less burdensome since the production support teams don't need to develop expertise in multiple languages.`,
									title: `Common Language`,
									image: <Image src={engineeringImg} lazy size="lg" />
								},
								{
									blurb: `Writing an entire application in a single language increases the flexibility of development teams. Team members can easily be shifted between different services in response to changing workloads. On large projects, we will often identify individual engineers to take the lead on each service. These engineers are encouraged to dig deep and specialize in their areas of responsibility. We then support these "service leads" with a pool of generalists who can pitch in wherever they are needed. This specialist/generalist model allows us to maintain high standards while also granting us the agility that modern development projects require.`,
									title: `Team Efficiency`,
									image: <Image src={teamImg} lazy size="lg" />
								},
								{
									blurb: `Modern web applications are often hindered by the need to replicate code between the client and server. The data that is exchanged across this interface is identical, but the need to codify the data transfer objects in both the client's and server's languages forces this duplication. By adopting JavaScript as the server's language, the interface objects can be shared. This reduces initial development time, and it makes the application easier to update as features are added, and the interfaces are required to share more information. Similarly, many business rules such as validation logic can also be commonized throughout the application making the application leaner, more consistent, and reliable.`,
									title: `Code Reuse`,
									image: <Image src={reuseImg} lazy size="lg" />
								},
								{
									blurb: `When Node.js was released, JavaScript had its first real chance to act on the server-side. However, many developers believed that JavaScript's single-threaded model would keep it from being able to handle enterprise-grade applications. These concerns have proven unjustified in most use cases. JavaScript's extensive use of deferred code execution via event streams and promises has allowed it to be competitive with (and often more performant than) many well-established languages such as Java and C#.`,
									title: `High Performance`,
									image: <Image src={performanceImg} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Custom Software Development FAQs">
					{[
						{
							title: 'What Is Full Stack JavaScript Development?',
							content: (
								<virtual>
									<p>
										Full Stack JavaScript development is the practice of
										developing both the client and server sides of an
										application in JavaScript or TypeScript. These applications
										often use JavaScript for the database interactions, but that
										is usually implemented via an object-relational mapper
										instead of a JavaScript-based database, such as MongoDB.
									</p>
									<p>
										Developers of full stack JS applications can often execute
										development tasks throughout the application, writing the
										code for a feature on both the client and server. Due to the
										complexities of modern enterprise applications, many teams
										elect to have dedicated domain experts on the client and
										server sides. These experts serve as gatekeepers, ensuring
										that the code is consistent and of high quality.
									</p>
								</virtual>
							)
						},
						{
							title: `What's the Difference Between Full Stack Development and Other Types of Development?`,
							content: (
								<p>
									Traditional web application development involves the use of two
									or more languages in the application stack. JavaScript or
									TypeScript are almost always used on the client-side, whereas
									the server code may be written in Java, C#, or many other
									languages. Supporting multiple languages forces developers to
									duplicate code that transfers data across the client-server
									interface. Full stack development often benefits from other code
									reuse opportunities, such as sharing data validation logic and
									business rules.
								</p>
							)
						},
						{
							title: 'What Is the skill set of a Full Stack Developer?',
							content: (
								<virtual>
									<p>
										Writing an application in a single language streamlines the
										development process, but a full stack developer still
										requires many skills.
									</p>
									<p>
										At the minimum, front end development requires a solid
										understanding of HTML, CSS, JavaScript, and the chosen front
										end frameworks such as Angular, React, or VueJS. Engineers
										also need to understand user-interaction design, data
										validation, and RESTful API design strategies to be truly
										effective on the front end.
									</p>
									<p>
										Back end development requires a different set of skills than
										the front end. A backend framework, such as ExpressJS, often
										forms the server code's backbone, so developers must be
										well-skilled in this technology and Node.js's standard
										library. Writing efficient and maintainable server code also
										requires engineers to be well versed in RESTful API design,
										best practices to codify business rules, data validation,
										and database query optimization.
									</p>
								</virtual>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default FullStackDevelopment;
